/* iter55k — Phase 5: Editor Review viewer.
 *
 * Renders the completed editor review as:
 *   • Header: month + project + status badge
 *   • Summary letter card (Markdown-lite: newlines preserved)
 *   • Inline comments grouped by chapter, sorted by paragraph index
 *
 * Read-only. The user cannot edit the editor's notes — only view.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { humanServicesApi } from '@/lib/api';
import { toast } from 'sonner';
import { ScrollText, Loader2, MessageSquare, ArrowLeft, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function EditorReviewViewer({ open, onOpenChange, reviewId }) {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !reviewId) return;
    let cancelled = false;
    setLoading(true);
    humanServicesApi.reviewGet(reviewId)
      .then((r) => { if (!cancelled) setPayload(r); })
      .catch(() => { if (!cancelled) toast.error('Failed to load review.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, reviewId]);

  const review   = payload?.review;
  const comments = payload?.comments || [];

  // Group comments by chapter.
  const grouped = comments.reduce((acc, c) => {
    (acc[c.chapter_id] = acc[c.chapter_id] || []).push(c);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="editor-review-viewer">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-[14px]">
            <ScrollText className="h-4 w-4 text-primary" /> Editor review
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            {review && (
              <>
                {review.month_key} · {review.project_name || 'Untitled'} ·
                <span className="ml-1 font-medium capitalize">{review.status?.replace('_', ' ')}</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-[12px] text-muted-foreground">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
          </div>
        ) : !review ? (
          <div className="py-6 text-center text-[12px] text-muted-foreground">
            Review not found.
          </div>
        ) : (
          <div className="max-h-[65vh] overflow-y-auto pr-1">
            {review.summary_letter ? (
              <section className="rounded-md border border-border bg-parchment/40 p-4"
                       data-testid="editor-review-summary-letter">
                <div className="mb-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  Summary letter
                </div>
                <div className="whitespace-pre-wrap font-serif text-[13px] leading-relaxed">
                  {review.summary_letter}
                </div>
              </section>
            ) : (
              <div className="rounded-md border border-dashed border-border p-4 text-center text-[11.5px] text-muted-foreground">
                The editor hasn&apos;t written the summary letter yet.
              </div>
            )}

            {comments.length > 0 && (
              <section className="mt-5" data-testid="editor-review-comments-section">
                <div className="mb-2 flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <MessageSquare className="h-3 w-3" /> Inline comments · {comments.length}
                </div>
                <div className="space-y-4">
                  {Object.entries(grouped).map(([chapterId, list]) => (
                    <div key={chapterId} className="rounded-md border border-border">
                      <div className="border-b border-border bg-secondary/50 px-3 py-1.5 text-[11px] font-semibold">
                        Chapter <span className="font-mono">{chapterId}</span>
                      </div>
                      <ul className="divide-y divide-border">
                        {list.sort((a, b) => a.paragraph_index - b.paragraph_index).map((c) => (
                          <li key={c.id}
                              data-testid={`editor-comment-${c.id}`}
                              className="px-3 py-2 text-[12px]">
                            <div className="mb-1 text-[10px] text-muted-foreground">
                              ¶ {c.paragraph_index + 1}
                            </div>
                            <div className="whitespace-pre-wrap">{c.text}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        <div className="mt-2 flex items-center justify-between gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-[12px]"
            disabled={!reviewId}
            onClick={() => {
              onOpenChange(false);
              navigate(`/editor/review/${reviewId}`);
            }}
            data-testid="editor-review-open-workspace-btn"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open Full Markup Workspace
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => onOpenChange(false)}
                  data-testid="editor-review-viewer-close-btn">
            <ArrowLeft className="mr-1 h-3 w-3" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
